import { useEffect, useRef, useState } from 'react';

import { getMotorwayOffer } from 'PrivateApis';

import { APIResponseStatus } from 'Services/api.types';

import { SaleConfirmationSentryOpts } from '../../containers/app/documentCapture/DocumentCapture.helpers';
import {
  ConfirmSaleAllowedStatusesValue,
  DocumentCaptureReviewCompleteAllowedStatesValue,
  SaleConfirmedStatusesValue,
  viewTimelineStatusesValue,
} from '../../containers/app/documentCapture/DocumentCapture.types';

import useUrlVRM from './useUrlVRM';

export type StateSlug =
  | ConfirmSaleAllowedStatusesValue
  | SaleConfirmedStatusesValue
  | viewTimelineStatusesValue
  | DocumentCaptureReviewCompleteAllowedStatesValue;

export type UseConfirmMotorwayOffer = {
  acceptedBid?: Record<string, unknown>;
  closingBid?: Record<string, unknown>;
  data?: Record<string, unknown>;
  enquiryId?: number;
  enquiryTier?: Record<string, unknown>;
  features?: Record<string, unknown>;
  fields?: Record<string, unknown>;
  // eslint-disable-next-line camelcase
  filled_sections?: Record<string, unknown>[];
  id?: number;
  state?: {
    slug?: StateSlug;
  };
  status?: string;
  valuation?: Record<string, unknown>;
};

type UseMotorwayOffer = {
  offer: UseConfirmMotorwayOffer;
  refetch?: () => void;
  status: APIResponseStatus;
};

const useMotorwayOffer = (): UseMotorwayOffer => {
  const [offer, setOffer] = useState<UseConfirmMotorwayOffer>({});
  const { initialStatus, valuationRef, vrm } = useUrlVRM();
  const [status, setStatus] = useState(initialStatus);
  const [refetch, setRefetch] = useState(0);

  const cancelled = useRef<boolean>(false);

  const resolve = (data: UseConfirmMotorwayOffer) => {
    setOffer(data);
    setStatus(APIResponseStatus.SUCCESS);
  };

  const reject = () => {
    if (!cancelled.current || refetch > 0) {
      setOffer({});
      setStatus(APIResponseStatus.ERROR);
    }
  };

  useEffect(() => {
    const fetchData = async (getVrm: string, getValuationRef: number | string) => {
      setStatus(APIResponseStatus.LOADING);

      try {
        const data = await getMotorwayOffer(getVrm, getValuationRef);
        resolve(data);
      } catch (err) {
        window?.Sentry?.captureException?.(err, SaleConfirmationSentryOpts);
        reject();
      }
    };

    if (vrm && valuationRef) {
      fetchData(vrm, valuationRef);
    }

    return () => {
      // In case of this useEffect was interrupted by for example Aborted Promise
      // This will notify `reject` function to not perform any setState on unmounted component
      // and will therefore prevent memory leak React issue
      cancelled.current = true;
    };
  }, [vrm, refetch]); // eslint-disable-line react-hooks/exhaustive-deps -- we only want to refetch, if VRM changed

  return {
    offer,
    refetch: () => setRefetch((prev) => prev + 1),
    status,
  };
};

export default useMotorwayOffer;
